import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Autocomplete, Avatar, Chip, ListItem, ListItemAvatar, ListItemText, TextField } from '@mui/material'

import { Analyst } from '../../../../../types/Analyst'

type ReportAnalystsFieldProps = {
  value: string[]
  onChange: (analystIds: string[]) => void
  analysts: Analyst[]
  error?: boolean
  disabled?: boolean
}

export const ReportAnalystsField: FC<ReportAnalystsFieldProps> = ({ value, onChange, analysts, error, disabled }) => {
  const { t } = useTranslation()
  const analystsById = useMemo(() => new Map(analysts.map((analyst) => [analyst.id, analyst])), [analysts])
  // Inactive analysts can stay on a report but aren't offered.
  const options = useMemo(() => Array.from(new Set([...value, ...analysts.filter(({ inactive }) => !inactive).map(({ id }) => id)])), [analysts, value])
  const name = (id: string) => analystsById.get(id)?.name ?? t('internal-analysts:reports_unknown_analyst', { id }) ?? id

  return (
    <Autocomplete
      multiple
      options={options}
      value={value}
      onChange={(_event, analystIds) => onChange(analystIds)}
      getOptionLabel={name}
      filterSelectedOptions
      disabled={disabled}
      renderTags={(analystIds, getTagProps) =>
        analystIds.map((id, index) => (
          <Chip {...getTagProps({ index })} key={id} avatar={<Avatar alt={name(id)} src={analystsById.get(id)?.picture} />} label={name(id)} />
        ))
      }
      renderOption={(props, id) => (
        <ListItem {...props} key={id}>
          <ListItemAvatar>
            <Avatar alt={name(id)} src={analystsById.get(id)?.picture} />
          </ListItemAvatar>
          <ListItemText primary={name(id)} secondary={analystsById.get(id)?.title} />
        </ListItem>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          required
          label={t('internal-analysts:reports_analysts')}
          error={error}
          helperText={error ? t('internal-analysts:reports_analysts_required') : undefined}
        />
      )}
    />
  )
}
